import { inject, Injectable } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { defer, Observable, of } from 'rxjs';
import { catchError, first, switchMap, timeout } from 'rxjs/operators';
import { UserRegistrationService } from '../../modules/user/registration/shared/services/user-registration/user-registration.service';
import { AuthenticatedUserService } from '../../modules/user/shared/services/authenticated-user/authenticated-user.service';
import { UserStateService } from '../../modules/user/shared/services/user-state/user-state.service';
import { GuardHelper } from '../helpers/guard-helper/guard-helper';
import { AuthenticatedUser } from '../models/authenticated-user';

@Injectable({
    providedIn: 'root',
})
class GeneralAuthGuard {
    constructor(
        private readonly auth0Service: AuthService,
        private readonly authenticatedUserService: AuthenticatedUserService,
        private readonly userStateService: UserStateService,
        private readonly userRegistrationService: UserRegistrationService,
        private readonly router: Router,
    ) {}

    canActivate(): Observable<boolean | UrlTree> {
        return this.waitForAuthentication().pipe(
            switchMap((authenticated: boolean) => {
                if (!authenticated) {
                    return GuardHelper.getHomeUrl(this.router);
                }

                // If the user is authenticated, proceed with guard logic.
                // Use BehaviorSubject's value directly since it always holds the latest user information.
                const user: AuthenticatedUser | null =
                    this.userStateService.user$.getValue();

                return defer(() => {
                    // If the user is not set, fetch the authenticated user.
                    const user$ = user
                        ? of(user)
                        : this.authenticatedUserService.getFreshAuthenticatedUser();

                    return user$.pipe(
                        switchMap((authenticatedUser: AuthenticatedUser) =>
                            this.proceedWithAuthGuardLogic(authenticatedUser),
                        ),
                        catchError(() => GuardHelper.getErrorUrl(this.router)),
                    );
                });
            }),
            catchError(() => {
                return GuardHelper.getErrorUrl(this.router);
            }),
        );
    }

    /**
     * Haults the progression of the authentication check until a true value is emitted or a max timeout has elapsed.
     * After the elapsed timeout, we treat it as not authenticated.
     * @returns Whether the user is authenticated or not.
     */
    private waitForAuthentication(): Observable<boolean> {
        return this.auth0Service.isAuthenticated$.pipe(
            first(),
            timeout(5000), // Hault the authenciation check to give Auth0 time to catch up
            catchError(() => of(false)),
        );
    }

    /**
     * Determines whether a user can proceed to the requested page or should be redirected to the appropriate page.
     * If the user is authenticated, registered, and approved, they will pass the general authentication guard.
     * @param user
     * @returns
     */
    private proceedWithAuthGuardLogic(
        user: AuthenticatedUser,
    ): Observable<boolean | UrlTree> {
        if (!user) {
            return GuardHelper.getErrorUrl(this.router);
        } else if (user.isApproved) {
            // Allow access to route. More restrictions may be placed for role in other guards.
            return of(true);
        } else if (!user.isRegistered) {
            return GuardHelper.getRegistrationUrl(this.router);
        } else {
            // User has already registered but not approved. Log them out like the typical post-registration flow.
            this.userRegistrationService.registrationComplete();
            return of(false);
        }
    }
}

export const GeneralAuthGuardFn: CanActivateFn = (): Observable<
    boolean | UrlTree
> => {
    return inject(GeneralAuthGuard).canActivate();
};
